import {
  ConfirmationDialog,
  DataGrid,
  formatDate,
  useNotifications
} from '@ijoin/components';
import { useGetAdminCronJobs, usePostAdminCronJobTrigger } from '@ijoin/hooks';
import { InfoOutlined, PlayArrow } from '@mui/icons-material';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import { createFileRoute, redirect } from '@tanstack/react-router';

import { useState } from 'react';

type CronJob = {
  nextRunAt?: string;
  queueName: string;
  schedule: {
    every?: number;
    limit?: number;
    pattern?: string;
    type: 'cron' | 'interval';
    tz?: string;
  }[];
  silent: boolean;
};

const CRON_JOB_DETAILS: Record<string, string> = {
  'aggregate-billing-metrics':
    'Near month-end (last 3 days of the month only), reads every plan id and sends one SQS message per plan to the aggregateBillingMetrics queue with { endOfMonth, planId } so consumers aggregate billing metrics per plan. Per-plan failures are logged and skipped.',
  'aggregate-plan-metrics':
    'Every 5 minutes, finds plans with an LDI calc (PlanEnrollments.ldiCalcInitAt, last 3 hours) newer than their PlanMetrics.updatedAt and sends one { planId } message per plan to the aggregateMetrics queue, keyed by planId for FIFO grouping and dedup. This is how plan metrics catch up after a batch of participant calcs lands; single calcs and plans already aggregated are skipped.',
  'alerus-export-errors-report':
    'Queries RecordKeeperExportQueues (joined to Plans/IntegrationPlanMetadatas) for Alerus exports with status EXPORTED_WITH_ERRORS since yesterday, extracts participant names and failure messages from the stored JSON, and emails a support report. Read-only.',
  'allianz-annuity-rollover-to-ira':
    'Finds active annuity rollover transactions (transTypeCode AR, funds IJANNTRAD/IJANNROTH, amount > 0) in ParticipantRKTransactions, builds a full rollover record (SSN, address, plan info) per row, and sends each to the sendRolloverTransactionsDistributionRequest SQS queue.',
  'allianz-relius-transaction-pull':
    'Selects live annuity-offering plans with annuityIndexAllocations on the Retirement Edge annuity integration and a Relius Connect integration, and sends { planId } to the sendTransactionsToRetirementEdge SQS queue for each. Runs twice daily.',
  'ameritas-transaction-upload':
    'Reads pending transactionExportData rows, groups the transactions by integration, PGP-encrypts each group, and uploads to S3 (prod: the Ameritas OMNI bucket outbound/MA path; non-prod: the SFTP bucket out/transactions path). Deletes the processed rows afterward.',
  'annexus-file-processing-ar004':
    'Sends a single SQS message to the annexusFileProcessing queue with annexusTask AR004; the downstream consumer does the actual file work.',
  'annexus-file-processing-rk001':
    'Sends a single SQS message to the annexusFileProcessing queue with annexusTask RK001; the downstream consumer does the actual file work.',
  'annexus-file-processing-rk002':
    'Sends a single SQS message to the annexusFileProcessing queue with annexusTask RK002; the downstream consumer does the actual file work.',
  'annexus-file-processing-rk003':
    'Sends a single SQS message to the annexusFileProcessing queue with annexusTask RK003; the downstream consumer does the actual file work.',
  'annuity-carrier-account-creation':
    'Finds active AnnuityPolicyParticipants past their eligibleForAccountOnDate that are missing a carrier account id, calls the Retirement Edge API to create the account, and writes back the account id, allocations, and annuityAccountDetails.',
  'annuity-carrier-account-update':
    'For active AnnuityPolicyParticipants that already have a Retirement Edge account id, fetches full account details from the Retirement Edge API and refreshes the stored annuityAccountDetails payload.',
  'annuity-carrier-plan-update':
    'For every plan with an annuityCarrierPlanId, resolves the carrier recordkeeper via the primary advisor’s org, pulls plan and product details from the Retirement Edge API, and writes Plans.annuityProductDetails.',
  'annuity-funding-transfer':
    'Selects up to 5 managed-account participants (52+, PreTax annuity active, sweep due, positive external balance) and orchestrates a funding sweep: validates plan bond funds (emailing support on gaps), triggers an LDI calc, creates the Retirement Edge account if missing, then queues an ANNUITY_FUNDING_TRANSFER_EXPORT_TO_RELIUS job (AutoJobQueues row + jobs SQS message).',
  'api-key-lifecycle':
    'Marks active ApiKeys past their expiresAt as expired, and revokes active keys older than 30 days when the same advisory org already has a newer active key. DB-only.',
  'auto-force-out-delayed-candidate-alert':
    'Finds RolloverParticipants stuck in pending distribution status (never sent to provider, audited over 3 days ago) and emails support a list. Read-only.',
  'auto-task-schedule-alert':
    'Hourly check for active AutoTaskSchedules whose nextScheduledDate is more than 10 minutes overdue; emails support a table of the late schedules. Read-only.',
  'billing-metrics':
    'Pages through billable Relius Connect plans and pulls PLANSTATISTICS and WEBRPTPLAN from Relius for each, inserting one RecordKeepersMetrics row per source with employee counts and market value.',
  'campaign-processor':
    'Every 30s, reads due unprocessed CampaignMessages (sendAt <= now), sends each to the sendEmailCampaign FIFO SQS queue, and marks the row processed/Queued. This is what actually dispatches scheduled email campaigns.',
  'check-job':
    'The auto-task scheduler: every minute claims up to 5 due AutoTaskSchedules, resolves the matching live plans per task type (force-out, plan/participant sync, MAP default, ACT-only), bulk-inserts AutoJobQueues rows plus jobs SQS messages, then recomputes each schedule’s nextScheduledDate.',
  'check-rk-export-reprocess':
    'Looks for RecordKeeperExportQueues rows stuck in Export Started / Pending Export within the last year and emails support a table of them. Read-only.',
  'cleanup-old-auto-job-queues':
    'Deletes AutoJobQueues rows older than 6 months (jobEndDateTime) for CREATE_REPORT, PLAN_SYNC, PARTICIPANT_SYNC, and PARTICIPANT_LITE_SYNC task types, in batches of 100 (up to 10,000 selected per pass).',
  'create-aum-reports':
    'Monthly (2nd): for every selectable managed-account methodology, runs a large INSERT ... ON DUPLICATE KEY UPDATE into AUMReports aggregating live plans with active MAP programs, participant balances, recordkeeper metrics, and primary advisor info as of last month-end.',
  'delete-record-keepers-metrics':
    'Monthly purge: deletes RecordKeepersMetrics rows older than roughly four months in a single raw DELETE.',
  'expired-sessions-collector':
    'Every 2 minutes deletes WebSessions rows whose expiresAt has passed. DB-only.',
  'external-metrics-rollup':
    "On the 3rd of each month, enqueues one external-metrics-migration message for the previous month-end so the Omni plan metrics (MDMetrics) and the billing rollup (BillingMetrics) are rebuilt without a manual trigger. Runs after the month has closed so that month's PlanMetricSnapshots, written by aggregate-billing-metrics, are already in place. Carries no source selections, so it replaces only the MDMetrics rows whose planCorrelationId belongs to an Omni plan. Omni counts are read live rather than as-of the month end, so replaying it for an older month backdates today's numbers onto that month.",
  'finicity-account-refresh':
    'Nightly, for every participant with a finicityCustomerId, calls the Finicity aggregation API to refresh their linked accounts; resets finicityErrorCount on success, increments it on failure.',
  'franklin-fund-family-flag-update-file':
    'On-demand only: reads a local FamilyFlagUpdate.csv, resolves CUSIPs to tickers via Funds, and updates matching FundUniverses rows with excludeFromMAPFee and the CSV inception date.',
  'franklin-fund-family-load':
    'On-demand only: reads a local FundFamilyList.csv and inserts any FUND_FAMILY names not already present into FundFamilies.',
  'franklin-templeton-advisor-info-data-feed':
    'Tue-Sat data feed: streams a query of FT-methodology plans (created since 2024) with primary advisor, advisory org, and recordkeeping org details into a tab-delimited CSV, PGP-encrypts it, and uploads it to the Franklin Templeton SFTP server.',
  'franklin-templeton-plan-info-data-feed':
    'Tue-Sat data feed: streams FT-methodology plan info (sponsor, MA program status, participant counts, QDIA type, termination date) into a tab-delimited CSV, PGP-encrypts it, and uploads it to the Franklin Templeton SFTP server.',
  'franklin-templeton-plan-participants-data-feed':
    'Tue-Sat data feed: streams per-participant rows for FT-methodology plans (MA status, balances, retirement goal, DOB, salary, deferral, portfolio allocation) into a tab-delimited CSV, PGP-encrypts it, and uploads it to the Franklin Templeton SFTP server.',
  'franklin-templeton-sftp-plan-fund':
    'Tue-Sat data feed: aggregates ParticipantFundBalances per fund/plan into GOE vs non-GOE balances for FT-methodology plans, writes a CSV, PGP-encrypts it, and uploads it to the Franklin Templeton SFTP server.',
  'gate-samples':
    'Every 30s samples the Redis gating state for gated SQS queues (in-flight counts, pending depth, rate-limit counters, integration batch sizes, queue priorities) into GateSamples rows, prunes idle gates from the active set, and hourly deletes samples older than 14 days.',
  'ira-clarity-automatic-assignment':
    'Runs IraProspectsService.processAutomaticAssignments: finds terminated participants in enabled IRA prospect plans whose external balance falls within the advisor’s configured range, adds them as IRA prospects, and emails the advisor when new candidates were added.',
  'keda-scaling-metrics':
    'Every 30s computes desired pod counts per consumer deployment from queue depth (Redis pending sets for gated queues, SQS message counts otherwise) and publishes them as the DesiredPods CloudWatch metric for KEDA autoscaling.',
  'map-analysis':
    'Nightly, finds active managed-account participants whose nextMAPAnalysis date is due on OMNI/Relius/PCS integrations and sends each to the mapAllocation SQS queue with SEND_MAP_REANALYSIS_REALLOCATION, grouped by participant so nightly and manual reanalyses cannot race.',
  'map-auto-reenroll':
    'Daily MAP auto-reenrollment sweep: finds participants eligible under active automaticReenrollment programs and either queues them to the mapAutoReEnroll SQS queue or directly writes their ManagedAccountParticipants row (entryExitTypeCode A) with an opt-out deadline, always recording an AutoJobQueues audit row. Participants who missed the window trigger a support email.',
  'map-auto-reenroll-defaulting':
    'Defaults-in MAP participants whose opt-out deadline passed while AWAITING: sets entryExitTypeCode DEFAULTED_IN, creates AnnuityPolicyParticipants rows when the plan offers an annuity, then sends a SEND_MAP_REENROLL_REALLOCATION message to the mapAllocation SQS queue.',
  'map-fee-monthly-pull':
    'Monthly (2nd): pages through MAP participants with stored feeData and upserts a ManagedAccountParticipantBalances row for last month-end; for FT-methodology programs a >5% balance change also triggers a reanalysis message to the mapAllocation SQS queue.',
  'map-fee-pull':
    'Nightly, pages through Relius Connect MAP participants and sends each (SSN, reliusPlanId, integrationId, opt-out data) to the mapFeePullDefault SQS queue; the actual fee pull happens in the downstream consumer.',
  'map-next-analysis-date':
    'Backfills ManagedAccountParticipants.nextMAPAnalysis for active MAP participants on live OMNI/Relius plans where it is NULL, deriving the date from the participant’s date of birth.',
  'omni-transaction-file-late-alert':
    'Tue-Sat 12:15am ET checks that an "Omni Transaction File Processing" activity exists for the prior business day; if not, and the Omni feed has ever delivered file transactions before that day, emails support that the file is late or missing. That launch check is what stops a pre-launch feed alerting; it deliberately has no trailing window, since a bounded one empties during a long outage and silences the alert exactly when it matters. An ERROR activity also clears the day, since it still proves the file arrived and support was already emailed about the failure. Market holidays are not detected. Read-only.',
  'pac-life-transaction-pull':
    'Nightly, for live Relius Connect plans on the PIMCO/Pacific Life Income Horizon methodology, pulls TRANSLED transaction rows per participant from Relius SOAP, filters to Pac Life CUSIPs, and upserts them into ParticipantRKTransactions.',
  'penchecks-dummy-participants-cleanup':
    'Deletes IraProspectParticipants rows that duplicate a real Participants record (matched on SSN and DOB) in a single raw DELETE.',
  'process-pending-plan-integration-exports':
    'Every 30s releases RecordKeeperExportQueues rows in PENDING_EXPORT whose scheduledReleaseDateTime has passed, dispatching each to the omniExportPpt, pcsPptExport, or reliusExportPpt SQS queue by integration type, then flags them releasedForProcessing.',
  'record-keeper-export-queues-update':
    'One-time/on-demand backfill: re-parses every RecordKeeperExportQueues row’s enrollmentData JSON and updates its planId and advisoryOrganizationId from the embedded initialData.',
  'relius-archived-plan-reactivation':
    'Twice daily, finds archived Relius Connect plans whose last plan sync said "not an ijoin enabled plan", re-checks PLANVOICE via Relius SOAP, and for plans now iJoin-enabled sets them LIVE, queues a PLAN_SYNC AutoJobQueues row, and sends an on-demand plan import SQS message.',
  'relius-health-check':
    'Hourly calls testConnection on every Relius Connect integration and emails support a Relius health-check report if any connection fails.',
  'snapshot-plan-participant-metrics':
    'Monthly (2nd): per plan, deletes and re-inserts PlanParticipantMetricSnapshots for the previous month-end by copying the current PlanParticipantMetrics rows.',
  'sponsoring-organization':
    'Daily updateMany that forces the name of one hardcoded SponsoringOrganizations row (EIN 381957940) to "MERS of Michigan". Nothing else.',
  'sqs-monitor':
    'Every 30 minutes lists all SQS queues under the environment prefix and, if any hold messages, posts a formatted queue-depth table to a Slack channel.',
  'sqs-queue-stats':
    'Every 15s caches message counts for every configured SQS queue; every 30s inserts SqsQueueSamples rows, hourly prunes samples older than 14 days, and every 5 minutes reconciles Redis rate-limit pending sets against AWS-reported depths for gated queues.',
  'stale-jobs-cleanup':
    'Every 20 minutes cancels AutoJobQueues rows running longer than 12 hours (status CANCELLED, "maximum execution time reached") and reconciles stale ParticipantSyncStatuses rows by absorbing missing counts into participantFailedCnt.',
  'straddle-sweep-funding-transfer':
    'Daily, finds participants with a positive balance in a holding fund on a partial-allocation investment vehicle and queues a STRADDLE_SWEEP_MAP_FUNDING_BALANCE_TRANSFER job for each (AutoJobQueues row + jobs SQS message).',
  'straddle-sweep-identify':
    'Daily, per advisory organization, finds live plans whose investment vehicles all allow partial allocation with a managed-account path and queues a STRADDLE_PARTIAL_MAP_IDENTIFY job per plan (AutoJobQueues row + straddleSweepIdentifyMap SQS message).',
  'table-defragmentation':
    'Weekly (Sunday) runs ALTER TABLE ... FORCE, ALGORITHM=INPLACE, LOCK=NONE over a hardcoded list of ~30 tables to rebuild and defragment them; per-table failures are logged and the loop continues.',
  'tcg-participant-relius-sync':
    'Daily, for plans on one hardcoded integration holding TIAA funds, pulls XDATAPLANEE/TIAANLI rows from Relius SOAP and backfills Participants.externalParticipantId by SSN match for participants missing one.',
  'tiaa-rk-transactions-export':
    'Daily 4am ET builds the TIAA SPARK 2.2 MANAGED JSON file from unprocessed ParticipantRKTransactionsExports on Alerus plans with TIAA SIA funds, PGP-encrypts it, saves the encrypted file to annuity-integrations/tiaa-sia/archive/2.2/ in the SFTP bucket, uploads it to the TIAA SFTP host, and marks the rows processed. On schema-validation failure it uploads an empty file instead. Every run records a "Spark 2.2 File Transmission" activity per advisory organization (fileType MANAGED): SUCCESSFUL with 0 records when there is nothing to send, ERROR plus a support email when ids are missing, the upload fails, or an empty file was sent. A file delivered outside TIAA\'s 4 to 6am ET window is SUCCESSFUL_WITH_WARNINGS with the delivery time in the log info.',
  'tiaa-sia-balance-file-ingest':
    'Weekdays 7am ET picks up the newest TIAA SIA balance file from the SFTP S3 bucket, PGP-decrypts and validates it, creates TIAA SIA Daily Balance Ingest activities per live plan, archives it under archive/2.3/, then exports the balances as a CSV (also saved to archive/2.3/) to the Alerus SFTP server and records a Daily Balance File Transmission activity per advisory organization (ERROR plus a support email when the upload fails). Invalid files, files matching no live plan, and older files skipped for a newer one each record an ERROR ingest activity; invalid files move to error/. Files whose name or source id is not recognised are left in in/raw/.',
  'tiaa-sia-balance-file-late-alert':
    'Weekdays 8am ET checks whether a TIAA SIA Daily Balance Ingest activity was recorded since midnight ET; if not, emails support that the TIAA SIA daily balance file is late or missing. Read-only.',
  'tiaa-sia-balance-file-transmission-late-alert':
    'Weekdays 9am ET checks whether today (ET) has a SUCCESSFUL TIAA SIA Daily Balance Ingest activity but no Daily Balance File Transmission activity; if so, emails support that the Daily Balance File was not generated. Read-only.',
  'tiaa-spark-22-late-alert':
    'Weekdays 5am ET checks that every record keeper with a TIAA managed or target source id has a Spark 2.2 File Transmission activity for each of its files (MANAGED, NLI) since midnight ET; emails support listing any file and record keeper with none. Read-only.',
  'tiaa-spark-data-feed':
    'Daily 4am ET builds a TIAA SPARK 2.2 NLI JSON file per TIAA partner id from all ParticipantRKTransactions on TIAA target funds, validates it, PGP-encrypts it, saves the encrypted file to annuity-integrations/tiaa-sia/archive/2.2/ in the SFTP bucket, and uploads it to the TIAA SFTP host. Records a "Spark 2.2 File Transmission" activity per advisory organization (fileType NLI): SUCCESSFUL, SUCCESSFUL with 0 records for target record keepers with nothing to send, or ERROR plus a support email when validation, the SFTP connection or the upload fails. A file delivered outside TIAA\'s 4 to 6am ET window is SUCCESSFUL_WITH_WARNINGS with the delivery time in the log info.',
  'update-advisor-participant-snapshots':
    'Every 12 hours pages through all advisor/plan pairs and upserts PlanParticipationSnapshots with aggregated participant metrics (active/eligible/ineligible/terminated-with-balance counts and percents) per pair.',
  'update-plan-end-at':
    'Once a year (Dec 31) rolls Plans.endAt forward to next year’s end-of-year for every plan whose endAt falls on the current year’s final day.'
};

const formatSchedule = (schedule: CronJob['schedule']) =>
  schedule
    .map(s => {
      if (s.type === 'cron') {
        return `${s.pattern}${s.tz ? ` (${s.tz})` : ''}`;
      }

      if (typeof s.every === 'number') {
        const seconds = s.every / 1000;

        if (seconds >= 3600) {
          return `every ${seconds / 3600}h`;
        }

        if (seconds >= 60) {
          return `every ${seconds / 60}m`;
        }

        return `every ${seconds}s`;
      }

      return 'unknown';
    })
    .join(', ');

const CronJobsPage = () => {
  const notifications = useNotifications();
  const cronJobs = useGetAdminCronJobs();
  const triggerJob = usePostAdminCronJobTrigger({
    mutation: {
      onError: (_error, variables) => {
        notifications.error(`Failed to trigger "${variables.queueName}"`);
      },
      onSuccess: (_data, variables) => {
        notifications.success(`Job "${variables.queueName}" triggered`);
      }
    }
  });
  const [pendingQueueName, setPendingQueueName] = useState<null | string>(null);

  const onCancelTrigger = () => setPendingQueueName(null);

  const onConfirmTrigger = () => {
    if (pendingQueueName) {
      triggerJob.mutate({ queueName: pendingQueueName });
    }
    setPendingQueueName(null);
  };

  const columns: GridColDef<CronJob>[] = [
    {
      align: 'center',
      field: 'details',
      getActions: params => [
        <GridActionsCellItem
          data-testid={`info-${params.row.queueName}`}
          icon={
            <Tooltip
              title={
                <Stack spacing={1} sx={{ p: 0.5 }}>
                  {(
                    CRON_JOB_DETAILS[params.row.queueName] ??
                    'This job has no registered description.'
                  )
                    .split('\n\n')
                    .map(paragraph => (
                      <Typography key={paragraph} variant='body2'>
                        {paragraph}
                      </Typography>
                    ))}
                </Stack>
              }>
              <InfoOutlined />
            </Tooltip>
          }
          key='details'
          label='Details'
        />
      ],
      headerName: '',
      sortable: false,
      type: 'actions',
      width: 50
    },
    {
      field: 'queueName',
      flex: 2,
      headerName: 'Queue Name',
      minWidth: 240
    },
    {
      field: 'nextRunAt',
      flex: 1.5,
      headerName: 'Next Run',
      minWidth: 180,
      valueFormatter: value =>
        value ? formatDate(value, { format: 'MM/DD/YYYY h:mm:ss A' }) : '-'
    },
    {
      field: 'schedule',
      flex: 2,
      headerName: 'Schedule',
      minWidth: 200,
      sortable: false,
      valueGetter: (_value, row) => formatSchedule(row.schedule)
    },
    {
      field: 'actions',
      headerName: '',
      renderCell: params => (
        <Tooltip title='Trigger job'>
          <IconButton
            data-testid={`trigger-${params.row.queueName}`}
            onClick={() => setPendingQueueName(params.row.queueName)}>
            <PlayArrow />
          </IconButton>
        </Tooltip>
      ),
      sortable: false,
      width: 65
    }
  ];

  return (
    <Stack spacing={2} sx={{ height: '100%', p: 2 }}>
      <Typography variant='h5'>Cron Jobs</Typography>

      <DataGrid
        autoHeight={false}
        columns={columns}
        disableColumnMenu
        disableRowSelectionOnClick
        getRowId={row => row.queueName}
        initialState={{
          pagination: { paginationModel: { page: 0, pageSize: 25 } },
          sorting: { sortModel: [{ field: 'queueName', sort: 'asc' }] }
        }}
        loading={cronJobs.isFetching}
        pageSizeOptions={[15, 25, 50, 100]}
        pinnedColumns={{ left: ['details'], right: ['actions'] }}
        rows={cronJobs.data?.data ?? []}
        showColumnMenu
        slotProps={{
          root: {
            'data-component': 'dataGrid',
            'data-testid': 'cronJobs'
          }
        }}
        sx={{ flex: 1, minHeight: 0 }}
      />

      <ConfirmationDialog
        confirmText='Trigger'
        data-testid={
          pendingQueueName ? `trigger-${pendingQueueName}` : 'trigger'
        }
        isOpen={!!pendingQueueName}
        message={
          pendingQueueName
            ? `Are you sure you want to manually trigger ${pendingQueueName}?`
            : ''
        }
        onCancel={onCancelTrigger}
        onConfirm={onConfirmTrigger}
        title='Trigger Cron Job'
      />
    </Stack>
  );
};

export const Route = createFileRoute('/cron-jobs/')({
  beforeLoad: () => {
    if (localStorage.getItem('isAdmin') !== '1') {
      throw redirect({ to: '/organizations' });
    }
  },
  component: CronJobsPage
});
